import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  BarChart3, 
  Activity, 
  Settings, 
  Search,
  ChevronLeft,
  ChevronRight,
  Crown,
  Sparkles,
  User,
  Trash2,
  Edit,
  DollarSign,
  TrendingUp,
  Calendar,
  Mail,
  Shield,
  AlertTriangle,
  Check,
  X,
  Loader2,
  LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // User management state
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newTier, setNewTier] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load initial data
  useEffect(() => {
    if (user?.role === 'admin') {
      loadStats();
      loadUsers();
      loadActivities();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    }
  };

  const loadUsers = async (page = 1) => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
      setTotalUsers(response.data.total);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      const response = await api.get('/admin/activity?limit=50');
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers(1);
  };

  const handleTierChange = async () => {
    if (!selectedUser || !newTier) return;
    setActionLoading(true);
    try {
      await api.put(`/admin/users/${selectedUser.id}/tier`, { tier: newTier });
      toast.success(`User tier updated to ${newTier}`);
      setShowTierModal(false);
      setSelectedUser(null);
      loadUsers(currentPage);
      loadStats();
    } catch (error) {
      toast.error('Failed to update tier');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers(currentPage);
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    const badges = {
      free: <Badge variant="secondary">Free</Badge>,
      trial: <Badge className="bg-mint text-white">Trial</Badge>,
      basic: <Badge className="bg-ocean-blue text-white">Basic</Badge>,
      pro: <Badge className="bg-coral text-white">Pro</Badge>
    };
    return badges[tier] || badges.free;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-coral flex items-center justify-center">
                <Shield className="w-4 h-4 sm:w-6 sm:h-6" />
              </div>
              <div>
                <h1 className="font-bold text-sm sm:text-lg">Admin Dashboard</h1>
                <p className="text-[10px] sm:text-xs text-slate-400 hidden sm:block">Minnow School</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white hover:bg-white/10 text-xs sm:text-sm px-2 sm:px-4"
                onClick={() => navigate('/dashboard')}
              >
                <span className="hidden sm:inline">Exit Admin</span>
                <X className="w-4 h-4 sm:hidden" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white hover:bg-white/10 text-xs sm:text-sm px-2 sm:px-4"
                onClick={logout}
              >
                <span className="hidden sm:inline">Logout</span>
                <LogOut className="w-4 h-4 sm:hidden" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Tabs */}
        <div className="flex gap-1 sm:gap-2 mb-4 sm:mb-8 border-b border-border pb-3 sm:pb-4 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'activity', label: 'Activity', icon: Activity }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              size="sm"
              className={`${activeTab === tab.id ? 'bg-slate-900' : ''} text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap`}
            >
              <tab.icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 sm:space-y-8"
          >
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-6">
                  <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1 sm:gap-2">
                    <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Total Users</span>
                    <span className="sm:hidden">Users</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="text-xl sm:text-3xl font-bold text-slate-900">{stats.total_users}</div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                    +{stats.new_users_week} this week
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-6">
                  <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1 sm:gap-2">
                    <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Monthly Revenue</span>
                    <span className="sm:hidden">Revenue</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="text-xl sm:text-3xl font-bold text-slate-900">${stats.revenue?.mrr || 0}</div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                    {stats.by_tier?.basic || 0} Basic + {stats.by_tier?.pro || 0} Pro
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-6">
                  <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1 sm:gap-2">
                    <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Active Trials</span>
                    <span className="sm:hidden">Trials</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="text-xl sm:text-3xl font-bold text-slate-900">{stats.active_trials}</div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                    {stats.by_tier?.trial || 0} total trial users
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-6">
                  <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1 sm:gap-2">
                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Onboarding Rate</span>
                    <span className="sm:hidden">Onboard</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="text-xl sm:text-3xl font-bold text-slate-900">{stats.onboarding_rate}%</div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                    Completed onboarding
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Tier Breakdown */}
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">Users by Tier</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Distribution of users across subscription tiers</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                  <div className="text-center p-2 sm:p-4 rounded-xl bg-slate-100 dark:bg-slate-800">
                    <div className="text-lg sm:text-2xl font-bold text-slate-900">{stats.by_tier?.free || 0}</div>
                    <Badge variant="secondary" className="mt-1 sm:mt-2 text-[10px] sm:text-xs">Free</Badge>
                  </div>
                  <div className="text-center p-2 sm:p-4 rounded-xl bg-mint/10">
                    <div className="text-lg sm:text-2xl font-bold text-slate-900">{stats.by_tier?.trial || 0}</div>
                    <Badge className="bg-mint mt-1 sm:mt-2 text-[10px] sm:text-xs">Trial</Badge>
                  </div>
                  <div className="text-center p-2 sm:p-4 rounded-xl bg-ocean-blue/10">
                    <div className="text-lg sm:text-2xl font-bold text-slate-900">{stats.by_tier?.basic || 0}</div>
                    <Badge className="bg-ocean-blue mt-1 sm:mt-2 text-[10px] sm:text-xs">Basic</Badge>
                  </div>
                  <div className="text-center p-2 sm:p-4 rounded-xl bg-coral/10">
                    <div className="text-lg sm:text-2xl font-bold text-slate-900">{stats.by_tier?.pro || 0}</div>
                    <Badge className="bg-coral mt-1 sm:mt-2 text-[10px] sm:text-xs">Pro</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Auth Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
              <Card>
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-sm sm:text-base">Auth Provider</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="flex items-center gap-2">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4" /> 
                        <span className="hidden sm:inline">Email/Password</span>
                        <span className="sm:hidden">Email</span>
                      </span>
                      <span className="font-semibold">{stats.auth_breakdown?.email || 0}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="flex items-center gap-2">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google
                      </span>
                      <span className="font-semibold">{stats.auth_breakdown?.google || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-sm sm:text-base">Growth</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span>New this week</span>
                      <Badge variant="outline" className="text-xs">{stats.new_users_week}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>New this month</span>
                      <Badge variant="outline" className="text-xs">{stats.new_users_month}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3 sm:space-y-4"
          >
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 text-sm"
                  />
                </div>
                <Button type="submit" size="sm">Search</Button>
              </form>
              <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); loadUsers(1); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Filter by tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Users Table - Desktop */}
            <Card className="hidden md:block">
              <CardContent className="p-0">
                {usersLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>Trial Days</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Auth</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-ocean-blue/10 flex items-center justify-center">
                                <User className="w-4 h-4 text-ocean-blue" />
                              </div>
                              <div>
                                <p className="font-medium">{u.name}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getTierBadge(u.tier)}</TableCell>
                          <TableCell>
                            {u.tier === 'trial' ? (
                              <span className={u.trial_status?.days_remaining <= 2 ? 'text-coral font-semibold' : ''}>
                                {u.trial_status?.days_remaining || 0} days
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(u.created_at)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {u.auth_provider === 'google' ? 'Google' : 'Email'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setNewTier(u.tier);
                                  setShowTierModal(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-coral hover:text-coral"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Users Cards - Mobile */}
            <div className="md:hidden space-y-3">
              {usersLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
                </div>
              ) : (
                users.map((u) => (
                  <Card key={u.id} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-ocean-blue/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-ocean-blue" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            setSelectedUser(u);
                            setNewTier(u.tier);
                            setShowTierModal(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-coral hover:text-coral"
                          onClick={() => {
                            setSelectedUser(u);
                            setShowDeleteModal(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      {getTierBadge(u.tier)}
                      <Badge variant="outline">
                        {u.auth_provider === 'google' ? 'Google' : 'Email'}
                      </Badge>
                      {u.tier === 'trial' && (
                        <span className={`${u.trial_status?.days_remaining <= 2 ? 'text-coral font-semibold' : 'text-muted-foreground'}`}>
                          {u.trial_status?.days_remaining || 0} days left
                        </span>
                      )}
                      <span className="text-muted-foreground">{formatDate(u.created_at)}</span>
                    </div>
                  </Card>
                ))
              )}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Showing {users.length} of {totalUsers} users
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => loadUsers(currentPage - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="flex items-center px-2 sm:px-3 text-xs sm:text-sm">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => loadUsers(currentPage + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">Recent Activity</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Latest signups and admin actions</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="space-y-3 sm:space-y-4">
                  {activities.map((activity, index) => (
                    <div key={index} className="flex items-start gap-2 sm:gap-4 p-2 sm:p-3 rounded-lg bg-muted/50">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'signup' ? 'bg-mint/20 text-mint' :
                        activity.type === 'admin_action' ? 'bg-coral/20 text-coral' :
                        'bg-ocean-blue/20 text-ocean-blue'
                      }`}>
                        {activity.type === 'signup' ? <User className="w-3 h-3 sm:w-4 sm:h-4" /> :
                         activity.type === 'admin_action' ? <Shield className="w-3 h-3 sm:w-4 sm:h-4" /> :
                         <Activity className="w-3 h-3 sm:w-4 sm:h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        {activity.type === 'signup' ? (
                          <>
                            <p className="font-medium text-sm sm:text-base">New signup: {activity.user_name}</p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">
                              {activity.user_email} via {activity.auth_provider}
                            </p>
                          </>
                        ) : activity.type === 'admin_action' ? (
                          <>
                            <p className="font-medium text-sm sm:text-base">
                              Admin: {activity.action?.replace(/_/g, ' ')}
                            </p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">
                              By {activity.admin}
                            </p>
                          </>
                        ) : (
                          <p className="font-medium text-sm sm:text-base">{activity.type}</p>
                        )}
                        <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                          {formatDate(activity.timestamp)}
                        </p>
                      </div>
                      {activity.tier && <div className="hidden sm:block">{getTierBadge(activity.tier)}</div>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Change Tier Modal */}
      <Dialog open={showTierModal} onOpenChange={setShowTierModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Change User Tier</DialogTitle>
            <DialogDescription className="text-sm">
              Update subscription tier for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newTier} onValueChange={setNewTier}>
              <SelectTrigger>
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="basic">Basic ($29/mo)</SelectItem>
                <SelectItem value="pro">Pro ($99/mo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowTierModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleTierChange} disabled={actionLoading} className="w-full sm:w-auto">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-coral text-base sm:text-lg">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
              Delete User Account
            </DialogTitle>
            <DialogDescription className="text-sm">
              Are you sure you want to delete this account? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 p-3 sm:p-4 rounded-lg bg-coral/10 border border-coral/20">
            <p className="text-sm font-medium">{selectedUser?.name}</p>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{selectedUser?.email}</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={actionLoading} className="w-full sm:w-auto">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
